import { Link, useLocation } from "react-router-dom";
import { format } from "date-fns";
import { useUpcomingEvents } from "../../lib/events";
import { useArticles } from "../../lib/articles";
import { modalLinkState } from "../../lib/modal-routing";

/**
 * Two side-by-side strips below the hero/marquee on the landing page:
 *   - Upcoming events (left, 3 items) — links to /calendar/<slug>
 *   - Latest news (right, 3 items) — links to /news/<slug>
 *
 * Both columns render an editorial skeleton (static muted rectangles, no
 * animate-pulse) on first paint while the queries resolve. The skeleton
 * matches the live row geometry exactly so there's no layout jump on resolve.
 */

const EVENT_SKEL_ROWS = [
  { title: "92%", summary: "78%" },
  { title: "78%", summary: "64%" },
  { title: "88%", summary: "72%" },
] as const;

const NEWS_SKEL_ROWS = [
  { title: "88%", subtitle: "72%" },
  { title: "76%", subtitle: "60%" },
  { title: "84%", subtitle: "68%" },
] as const;

export function LandingPreviews() {
  const location = useLocation();
  const events = useUpcomingEvents({ limit: 3, days: 60 });
  const news = useArticles({ limit: 3 });

  const evs = events.data?.events ?? [];
  const arts = news.data?.articles ?? [];

  const showEventsSkeleton = events.isLoading && evs.length === 0;
  const showNewsSkeleton = news.isLoading && arts.length === 0;

  // After loading: both empty → render nothing so we don't dead-band.
  if (
    !showEventsSkeleton &&
    !showNewsSkeleton &&
    evs.length === 0 &&
    arts.length === 0
  ) {
    return null;
  }

  return (
    <section className="py-14 md:py-20 border-b border-[var(--color-rule)]">
      <div className="container-edit grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16">
        {/* Events */}
        <div>
          <div className="flex items-baseline justify-between mb-7">
            <p className="eyebrow">Upcoming</p>
            <Link
              to="/calendar"
              className="font-grotesk text-[0.7rem] tracking-[0.18em] uppercase text-[var(--color-ink-mute)] hover:text-[var(--color-violet)] transition-colors"
            >
              All →
            </Link>
          </div>
          {showEventsSkeleton ? (
            <EventsSkeleton />
          ) : evs.length === 0 ? (
            <p className="font-display-italic text-[1.1rem] text-[var(--color-ink-mute)]">
              The next gathering is being prepared.
            </p>
          ) : (
            <ul className="flex flex-col">
              {evs.map((ev, idx) => (
                <li
                  key={ev.id}
                  className="grid grid-cols-[60px_1fr] sm:grid-cols-[72px_1fr] gap-3 sm:gap-4 py-4 border-t border-[var(--color-rule)]"
                  style={idx === 0 ? { borderTop: "none", paddingTop: 0 } : undefined}
                >
                  <div className="text-right">
                    <p className="font-display-italic text-[var(--color-violet)] text-[0.9rem] leading-[1] mb-1">
                      {format(new Date(ev.startsAt), "MMM").toLowerCase()}
                    </p>
                    <p className="font-display lining text-[1.75rem] leading-[0.9] tracking-tight">
                      {format(new Date(ev.startsAt), "dd")}
                    </p>
                  </div>
                  <div className="border-l border-[var(--color-rule)] pl-4 min-w-0">
                    <p className="font-grotesk text-[0.6rem] tracking-[0.18em] uppercase text-[var(--color-violet)] mb-1">
                      {ev.category.replace(/-/g, " ")}
                      <span className="mx-2 text-[var(--color-ink-mute)]">·</span>
                      <span className="text-[var(--color-ink-mute)]">
                        {ev.allDay ? "All day" : ev.timeTbd ? "Time TBA" : format(new Date(ev.startsAt), "HH:mm")}
                      </span>
                      {ev.location && (
                        <>
                          <span className="mx-2 text-[var(--color-ink-mute)]">·</span>
                          <span className="text-[var(--color-ink-mute)]">{ev.location}</span>
                        </>
                      )}
                    </p>
                    <Link
                      to={`/calendar/${ev.slug}`}
                      state={modalLinkState(location)}
                      className="font-display text-[1.2rem] sm:text-[1.3rem] leading-[1.15] tracking-[-0.01em] hover:text-[var(--color-violet)] transition-colors"
                    >
                      {ev.title}
                    </Link>
                    {ev.summary && (
                      <p className="text-[0.88rem] leading-[1.45] text-[var(--color-ink-soft)] mt-1 line-clamp-1">
                        {ev.summary}
                      </p>
                    )}
                  </div>
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* News */}
        <div>
          <div className="flex items-baseline justify-between mb-7">
            <p className="eyebrow">Latest</p>
            <Link
              to="/news"
              className="font-grotesk text-[0.7rem] tracking-[0.18em] uppercase text-[var(--color-ink-mute)] hover:text-[var(--color-violet)] transition-colors"
            >
              All news →
            </Link>
          </div>
          {showNewsSkeleton ? (
            <NewsSkeleton />
          ) : arts.length === 0 ? (
            <p className="font-display-italic text-[1.1rem] text-[var(--color-ink-mute)]">
              First reflection is being written.
            </p>
          ) : (
            <ul className="flex flex-col">
              {arts.map((a, idx) => (
                <li
                  key={a.id}
                  className="flex gap-4 items-start py-4 border-t border-[var(--color-rule)]"
                  style={idx === 0 ? { borderTop: "none", paddingTop: 0 } : undefined}
                >
                  <div className="flex-1 min-w-0">
                    <p className="font-grotesk text-[0.6rem] tracking-[0.18em] uppercase text-[var(--color-violet)] mb-1">
                      {a.category.replace(/-/g, " ")}
                      <span className="mx-2 text-[var(--color-ink-mute)]">·</span>
                      <span className="text-[var(--color-ink-mute)]">
                        {a.publishedAt ? format(new Date(a.publishedAt), "d LLL yyyy") : ""}
                      </span>
                    </p>
                    <Link
                      to={`/news/${a.slug}`}
                      className="font-display text-[1.2rem] sm:text-[1.3rem] leading-[1.15] tracking-[-0.01em] hover:text-[var(--color-violet)] transition-colors"
                    >
                      {a.title}
                    </Link>
                    {a.subtitle ? (
                      <p className="font-display-italic text-[0.9rem] text-[var(--color-ink-mute)] mt-1 line-clamp-1">
                        {a.subtitle}
                      </p>
                    ) : (
                      a.excerpt && (
                        <p className="text-[0.88rem] leading-[1.45] text-[var(--color-ink-soft)] mt-1 line-clamp-1">
                          {a.excerpt}
                        </p>
                      )
                    )}
                  </div>
                  {a.coverImage && (
                    <Link
                      to={`/news/${a.slug}`}
                      tabIndex={-1}
                      aria-hidden="true"
                      className="shrink-0"
                    >
                      <img
                        src={a.coverImage}
                        alt=""
                        className="w-[80px] h-[80px] sm:w-[96px] sm:h-[96px] md:w-[110px] md:h-[110px] object-cover"
                      />
                    </Link>
                  )}
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </section>
  );
}

function EventsSkeleton() {
  return (
    <ul className="flex flex-col" aria-busy="true" aria-label="Loading upcoming events">
      {EVENT_SKEL_ROWS.map((widths, i) => (
        <li
          key={i}
          className="grid grid-cols-[60px_1fr] sm:grid-cols-[72px_1fr] gap-3 sm:gap-4 py-4 border-t border-[var(--color-rule)]"
          style={i === 0 ? { borderTop: "none", paddingTop: 0 } : undefined}
        >
          <div className="text-right">
            <div className="skel-block ml-auto" style={{ width: 28, height: 9, marginBottom: 6 }} />
            <div className="skel-block ml-auto" style={{ width: 38, height: 24 }} />
          </div>
          <div className="border-l border-[var(--color-rule)] pl-4 min-w-0">
            <div className="skel-block" style={{ width: 90, height: 8, marginBottom: 10 }} />
            <div className="skel-block" style={{ width: widths.title, height: 16, marginBottom: 8 }} />
            <div className="skel-block" style={{ width: widths.summary, height: 10 }} />
          </div>
        </li>
      ))}
    </ul>
  );
}

function NewsSkeleton() {
  return (
    <ul className="flex flex-col" aria-busy="true" aria-label="Loading latest news">
      {NEWS_SKEL_ROWS.map((widths, i) => (
        <li
          key={i}
          className="flex gap-4 items-start py-4 border-t border-[var(--color-rule)]"
          style={i === 0 ? { borderTop: "none", paddingTop: 0 } : undefined}
        >
          <div className="flex-1 min-w-0">
            <div className="skel-block" style={{ width: 110, height: 8, marginBottom: 10 }} />
            <div className="skel-block" style={{ width: widths.title, height: 16, marginBottom: 8 }} />
            <div className="skel-block" style={{ width: widths.subtitle, height: 10 }} />
          </div>
          <div
            className="skel-block shrink-0 w-[80px] h-[80px] sm:w-[96px] sm:h-[96px] md:w-[110px] md:h-[110px]"
            aria-hidden
          />
        </li>
      ))}
    </ul>
  );
}
